/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 *
 * Singleton for the google maps api. Used to ensure that the google maps api is loaded once when needed.
 * Also, the instance of google maps should be the same in order to avoid the creation of multiple different maps
 * in the application or unnecessary reloads.
 */
import React, { CSSProperties, useEffect, useState } from 'react';
import { HoverData, HoverDataChangedEvent } from './useBeamGeoJsonLayers';
import { bpsToUnitString } from '../../../utils/DataRateUtils';
import { darkTheme, getTooltipTextWithHnaAltitude } from '../../../utils/BeamUtils';
import appTheme from '../theme/appTheme.json';

export type GeoJSONMapTooltipProps = {
  hoverDataEventTarget: EventTarget;
  map: google.maps.Map;
  highlightColor?: number[] | ((feature: any) => number[]);
  tooltipStyle?: CSSProperties;
  disableTooltip?: boolean;
  tooltipText?: (feature: any) => string;
};

/**
 * Tooltip component for the beams rendered on the map from useBeamGeoJsonLayers.
 * @param props - An object containing styling options for the tooltip as well as an event target to listen for hover events.
 * @returns A React component that displays a tooltip with information about the currently hovered geojson beam.
 */
const GeoJSONMapTooltip: React.FC<GeoJSONMapTooltipProps> = (props: GeoJSONMapTooltipProps) => {
  const {
    hoverDataEventTarget,
    map,
    highlightColor = darkTheme.highlightColor,
    tooltipStyle = { fontFamily: 'sans-serif' },
    disableTooltip = false,
    tooltipText = defaultBeamsTooltipText,
  } = props;
  const [hoverData, setHoverData] = useState<HoverData>({ hoveredObject: {}, pointerX: 0, pointerY: 0 });
  // We want to listen to these events to clear the hover data
  const eventsToListenFor = ['mouseout', 'zoom_changed', 'drag'];

  useEffect(() => {
    const handleHoverDataChanged = (event: CustomEvent) => {
      setHoverData(event.detail);
    };
    const clearHover = () => {
      setHoverData({ hoveredObject: {}, pointerX: 0, pointerY: 0 });
    };
    // Listen to the CustomEvents dispatched by the useBeamGeoJsonLayers hook
    hoverDataEventTarget.addEventListener(HoverDataChangedEvent, handleHoverDataChanged as EventListener);

    // Add the events to listen for so we can clear hover data
    const eventListeners = eventsToListenFor.map(event => map.addListener(event, clearHover));

    // Cleanup the listeners
    return () => {
      hoverDataEventTarget.removeEventListener(HoverDataChangedEvent, handleHoverDataChanged as EventListener);
      eventListeners.forEach(listener => listener.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverDataEventTarget]);

  return hoverData.hoveredObject && !disableTooltip && Object.keys(hoverData.hoveredObject).length > 0 ? (
    <div
      data-test="beam_tooltip"
      style={{
        position: 'absolute',
        zIndex: 5,
        pointerEvents: 'none',
        left: hoverData.pointerX,
        top: hoverData.pointerY,
        backgroundColor: appTheme.primaryBackgroundColor,
        border: `3px solid ${rgbToHex(
          highlightColor instanceof Function ? highlightColor(hoverData.hoveredObject) : highlightColor,
        )}`,
        color: '#FFFFFF',
        fontSize: '12px',
        padding: '4px 8px',
        whiteSpace: 'nowrap',
        borderRadius: '16px',
        ...tooltipStyle,
      }}
    >
      {tooltipText(hoverData.hoveredObject)}
    </div>
  ) : null;
};

export default GeoJSONMapTooltip;

const defaultBeamsTooltipText = (feature: any) => (feature.properties || {}).displayName;
export const beamsTooltipTextWithData = (feature: any) => {
  if (feature.properties.rlDataRateBps) {
    return `${feature.properties.beamDisplayName} - ${bpsToUnitString(feature.properties.rlDataRateBps)}`;
  } else if (feature.properties.flDataRateBps) {
    return `${feature.properties.beamDisplayName} - ${bpsToUnitString(feature.properties.flDataRateBps)}`;
  } else {
    return getTooltipTextWithHnaAltitude(feature);
  }
};

export const componentToHex = (c: number) => {
  let hex = c.toString(16);

  return hex.length === 1 ? '0' + hex : hex;
};

export const rgbToHex = (rgb: number[]) => {
  return `#${componentToHex(rgb[0])}${componentToHex(rgb[1])}${componentToHex(rgb[2])}`;
};
